import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { legalService, LegalStatusItem } from '@/services/legalService'

const LABELS: Record<string, { name: string; href: string }> = {
    TERMS: { name: 'Términos y Condiciones', href: '/terms' },
    PRIVACY: { name: 'Política de Privacidad', href: '/privacy' },
    AI_DISCLOSURE: { name: 'Aviso de uso de IA', href: '/ai-disclosure' },
}

/**
 * Bloquea la app tras el login hasta que el usuario acepte los documentos
 * legales vigentes (versión + hash). Registra la aceptación en el backend.
 * (Blindaje legal — ítems 1, 2, 3, 8)
 */
export default function TermsGate() {
    const { isAuthenticated } = useAuth()
    const [pending, setPending] = useState<LegalStatusItem[]>([])
    const [checked, setChecked] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!isAuthenticated) {
            setPending([])
            return
        }
        let active = true
        legalService
            .getStatus()
            .then((s) => active && setPending(s.documents.filter((d) => !d.accepted)))
            .catch(() => {
                /* si el endpoint falla, no bloqueamos el uso */
            })
        return () => {
            active = false
        }
    }, [isAuthenticated])

    if (!isAuthenticated || pending.length === 0) return null

    const acceptAll = async () => {
        setSubmitting(true)
        setError(null)
        try {
            for (const d of pending) {
                await legalService.accept(d.type, d.version, d.hash)
            }
            setPending([])
        } catch {
            setError('No se pudo registrar la aceptación. Reintente.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-lg w-full p-6">
                <h2 className="text-xl font-bold text-white mb-3">Documentos legales</h2>
                <p className="text-sm text-gray-300 mb-4">
                    Para continuar, revisa y acepta los siguientes documentos vigentes:
                </p>
                <ul className="space-y-2 mb-4">
                    {pending.map((d) => {
                        const meta = LABELS[d.type] ?? { name: d.type, href: '#' }
                        return (
                            <li key={d.type} className="text-sm">
                                <a
                                    href={meta.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-indigo-400 underline"
                                >
                                    {meta.name}
                                </a>{' '}
                                <span className="text-gray-500">(v{d.version})</span>
                            </li>
                        )
                    })}
                </ul>

                <label className="flex items-start gap-2 text-sm text-gray-300 mb-4">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                        className="mt-1"
                    />
                    <span>He leído y acepto los documentos indicados.</span>
                </label>

                {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

                <button
                    disabled={!checked || submitting}
                    onClick={acceptAll}
                    className="w-full px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                    {submitting ? 'Registrando…' : 'Aceptar y continuar'}
                </button>
            </div>
        </div>
    )
}
